import React, { useState, useEffect } from 'react';
import { Users, Wifi, Sparkles, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

const FacilitiesEquipment = () => {
  const [selectedSection, setSelectedSection] = useState(null);

  // Top stats data
  const topStats = [
    { label: 'Total Guests', value: '19,580', subLabel: '100% Capacity', icon: Users, iconBg: 'bg-orange-500/20', iconColor: 'text-orange-400', trend: 'up' },
    { label: 'Device Count', value: '18,742', subLabel: '96% Connected', icon: Wifi, iconBg: 'bg-cyan-500/20', iconColor: 'text-cyan-400', trend: 'up' },
    { label: 'Arena Health', value: '4.7', subLabel: 'Excellent Status', icon: Sparkles, iconBg: 'bg-purple-500/20', iconColor: 'text-purple-400', trend: 'up', isHealth: true },
  ];

  // Metrics data
  const metrics = [
    { label: 'Availability', value: 91, unit: '%', change: '+2.3%', trend: 'up', color: 'text-cyan-400' },
    { label: 'Accessibility', value: 95, unit: '%', change: '+1.8%', trend: 'up', color: 'text-purple-400' },
    { label: 'Retainability', value: 99, unit: '%', change: '+0.5%', trend: 'up', color: 'text-green-400' },
    { label: 'Throughput', value: 35, unit: 'Mbps', change: '-3.2%', trend: 'down', color: 'text-orange-400' },
    { label: 'Traffic', value: 253, unit: 'GB', change: '+12.5%', trend: 'up', color: 'text-purple-400' },
    { label: 'Utilization', value: 78, unit: '%', change: '+5.1%', trend: 'up', color: 'text-cyan-400' },
  ];

  // Entry gates data
  const entryGates = [
    { name: 'Gate A', count: 4250, color: 'bg-green-500' },
    { name: 'Gate B', count: 3920, color: 'bg-blue-500' },
    { name: 'Gate C', count: 3520, color: 'bg-purple-500' },
    { name: 'Gate D', count: 3130, color: 'bg-orange-500' },
    { name: 'Other', count: 4760, color: 'bg-gray-500' },
  ];

  // Traffic & Guests data
  const trafficData = [
    { area: 'Lower Bowl', traffic: 8521, guests: 7250, change: '+5.2%', trend: 'up' },
    { area: 'Upper Bowl', traffic: 5234, guests: 4820, change: '+2.1%', trend: 'up' },
    { area: 'Club Seats', traffic: 2145, guests: 1980, change: '-1.3%', trend: 'down' },
    { area: 'Suites', traffic: 1542, guests: 1420, change: '+3.4%', trend: 'up' },
    { area: 'Concourse', traffic: 3845, guests: 3200, change: '+8.7%', trend: 'up' },
  ];

  // Arena sections
  const arenaSections = [
    { id: '101-110', position: 'top', occupancy: 98, capacity: 542652 },
    { id: '111-120', position: 'bottom', occupancy: 85, capacity: 480000 },
    { id: '121-130', position: 'left', occupancy: 72, capacity: 320000 },
    { id: '131-140', position: 'right', occupancy: 91, capacity: 410000 },
  ];

  // Mini chart SVG generator
  const MiniChart = ({ color, trend }) => (
    <svg className="w-full h-12" viewBox="0 0 100 40">
      <path
        d={trend === 'up' 
          ? "M 0 30 Q 20 25 30 28 T 50 20 T 70 22 T 100 10" 
          : "M 0 15 Q 20 18 30 20 T 50 25 T 70 22 T 100 30"}
        fill="none"
        stroke={color}
        strokeWidth="2"
      />
    </svg>
  );

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white">TD Garden Arena</h1>
          <p className="text-gray-400 mt-1">Arena Analytics & Operations Dashboard</p>
        </div>

        {/* Top Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {topStats.map((stat, idx) => (
            <div key={idx} className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-400 text-sm">{stat.label}</span>
                <div className={`p-2 rounded-lg ${stat.iconBg}`}>
                  <stat.icon size={18} className={stat.iconColor} />
                </div>
              </div>
              <p className={`text-4xl font-bold text-white mb-1 ${stat.isHealth ? 'text-green-400' : ''}`}>
                {stat.value}
              </p>
              <p className={`text-sm ${stat.isHealth ? 'text-green-400' : 'text-green-400'}`}>
                ↗ {stat.subLabel}
              </p>
            </div>
          ))}
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-6 gap-4 mb-6">
          {metrics.map((metric, idx) => (
            <div key={idx} className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
              <span className="text-gray-400 text-xs">{metric.label}</span>
              <p className="text-3xl font-bold text-white mt-1">
                {metric.value}<span className="text-gray-400 text-lg">{metric.unit}</span>
              </p>
              <div className="mt-2">
                <MiniChart color={metric.trend === 'up' ? '#22c55e' : '#f97316'} trend={metric.trend} />
              </div>
              <p className={`text-xs mt-1 ${metric.trend === 'up' ? 'text-green-400' : 'text-orange-400'}`}>
                {metric.trend === 'up' ? '↗' : '↘'} {metric.change}
              </p>
            </div>
          ))}
        </div>

        <div className="flex gap-6">
          {/* Arena Heat Map */}
          <div className="flex-1">
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-semibold text-lg">Arena Heat Map</h2>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded"></span> 90-100%</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded"></span> 70-90%</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-500 rounded"></span> 60-70%</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-500 rounded"></span> &lt;50%</span>
                </div>
              </div>

              {/* Section Tooltip */}
              {selectedSection && (
                <div className="absolute bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm z-10">
                  <p className="text-white font-medium">Section {selectedSection.id}</p>
                  <p className="text-gray-400">Occupancy: {selectedSection.occupancy}%</p>
                  <p className="text-gray-400">Capacity: {selectedSection.capacity.toLocaleString()}</p>
                </div>
              )}

              {/* Arena Layout */}
              <div className="relative">
                {/* Outer container with sections */}
                <svg viewBox="0 0 500 600" className="w-full h-auto">
                  {/* Blue outer ring */}
                  <rect x="20" y="20" width="460" height="560" rx="10" fill="#1e3a5f" stroke="#3b82f6" strokeWidth="2" />
                  
                  {/* Section labels - Top */}
                  <text x="250" y="60" textAnchor="middle" fill="white" fontSize="20" fontWeight="bold">101-110</text>
                  
                  {/* Orange ring */}
                  <rect x="50" y="80" width="400" height="440" rx="10" fill="#9a3412" stroke="#f97316" strokeWidth="2" />
                  
                  {/* Yellow ring */}
                  <rect x="80" y="120" width="340" height="360" rx="10" fill="#854d0e" stroke="#eab308" strokeWidth="2" />
                  
                  {/* Green ring */}
                  <rect x="110" y="160" width="280" height="280" rx="10" fill="#166534" stroke="#22c55e" strokeWidth="2" />
                  
                  {/* Dark court area */}
                  <rect x="150" y="200" width="200" height="200" rx="5" fill="#0f172a" />
                  
                  {/* Court floor (brown) */}
                  <rect x="170" y="240" width="160" height="160" fill="#78350f" />
                  
                  {/* Center court ellipse */}
                  <ellipse cx="250" cy="320" rx="50" ry="60" fill="none" stroke="#fef3c7" strokeWidth="2" />
                  <circle cx="250" cy="320" r="5" fill="#fef3c7" />
                  
                  {/* Section labels - Left */}
                  <text x="35" y="300" textAnchor="middle" fill="white" fontSize="16" fontWeight="bold" transform="rotate(-90, 35, 300)">121-130</text>
                  
                  {/* Section labels - Right */}
                  <text x="465" y="300" textAnchor="middle" fill="white" fontSize="16" fontWeight="bold" transform="rotate(90, 465, 300)">131-140</text>
                  
                  {/* Section labels - Bottom */}
                  <text x="250" y="560" textAnchor="middle" fill="white" fontSize="20" fontWeight="bold">111-120</text>
                  
                  {/* Basket hoops */}
                  <circle cx="250" cy="210" r="8" fill="none" stroke="white" strokeWidth="2" />
                  <circle cx="250" cy="430" r="8" fill="none" stroke="white" strokeWidth="2" />
                </svg>
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="w-80 flex-shrink-0 space-y-4">
            {/* Last Event Snapshot */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-4">Last Event Snapshot</h3>
              
              {/* Circular Gauge */}
              <div className="flex justify-center mb-4">
                <div className="relative w-32 h-32">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <circle cx="50" cy="50" r="45" fill="none" stroke="#1f2937" strokeWidth="8" />
                    <circle 
                      cx="50" cy="50" r="45" fill="none" 
                      stroke="#22c55e" strokeWidth="8"
                      strokeDasharray="283"
                      strokeDashoffset="17"
                      strokeLinecap="round"
                      transform="rotate(-90 50 50)"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold text-green-400">4.7</span>
                    <span className="text-xs text-gray-400">Health Rate</span>
                  </div>
                </div>
              </div>

              <div className="text-center mb-4">
                <p className="text-gray-400 text-sm">Overall Score</p>
                <p className="text-4xl font-bold text-white">94%</p>
              </div>

              {/* Entry Gates Distribution */}
              <h4 className="text-gray-400 text-sm mb-3">Entry Gates Distribution</h4>
              <div className="flex gap-1 mb-3">
                {entryGates.map((gate, idx) => (
                  <div 
                    key={idx}
                    className={`h-10 ${gate.color} rounded`}
                    style={{ width: `${(gate.count / 19580) * 100}%` }}
                  />
                ))}
              </div>
              <div className="space-y-2">
                {entryGates.map((gate, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${gate.color}`}></span>
                      <span className="text-gray-400">{gate.name}</span>
                    </span>
                    <span className="text-white">{gate.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Traffic & Guests */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-4">Traffic & Guests</h3>
              
              <div className="space-y-4">
                {trafficData.map((item, idx) => (
                  <div key={idx} className="border-b border-gray-800 pb-3 last:border-0 last:pb-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">{item.area}</span>
                      <span className={`text-xs ${item.trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                        {item.change}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <div>
                        <p className="text-gray-500">Traffic</p>
                        <p className="text-white">{item.traffic.toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-500">Guests</p>
                        <p className="text-white">{item.guests.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilitiesEquipment;
